import { FundRequestStatus, useListBirthdayRequest, useUpdateRequest, useGetBirthdayRequestDocument } from "@/lib/api/fund";
import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment-timezone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { CommonDestinationTimezone, formatCurrency, GenerateLoadingOrError } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { safeParseDecimal } from "@/lib/api/common";
import { getReadableStatusLabel, getStatusBadgeColor } from "../common";
import { Plus, Trash2 } from "lucide-react";

// Type for breakdown form data
type BreakdownFormData = {
  id?: string;
  name: string;
  amount: string;
  status: 'projected' | 'granted';
};


export function ViewBirthdayFundRequest() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const request_id = searchParams.get("request_id");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showRawContent, setShowRawContent] = useState(false);
  const [formData, setFormData] = useState({
    has_been_granted: false,
    has_been_dispersed: false,
    has_been_reconciled: false,
    nominated_organization: '',
    organization: '',
    nominated_name: '',
    notes: "",
    breakdowns: [] as BreakdownFormData[],
  });

  const requestResult = useListBirthdayRequest(request_id);
  const updateRequest = useUpdateRequest();
  const documentResult = useGetBirthdayRequestDocument(showRawContent ? request_id : null);

  const request = requestResult.data?.data;

  // Initialize form data when request loads
  useEffect(() => {
    if (request) {
      // Convert breakdowns to array format
      let breakdownsArray: BreakdownFormData[] = [];
      if (request.breakdowns) {
        // Handle both array and single object formats from API
        if (Array.isArray(request.breakdowns)) {
          breakdownsArray = request.breakdowns.map((b: any) => ({
            id: b.id,
            name: b.name,
            amount: b.amount || '',
            status: b.status as 'projected' | 'granted',
          }));
        } else if (request.breakdowns.id) {
          // Single object format - convert to array
          breakdownsArray = [{
            id: request.breakdowns.id,
            name: request.breakdowns.name,
            amount: request.breakdowns.amount || '',
            status: request.breakdowns.status as 'projected' | 'granted',
          }];
        }
      }

      setFormData({
        has_been_granted: request.has_been_granted,
        has_been_dispersed: request.has_been_dispersed,
        has_been_reconciled: request.has_been_reconciled,
        nominated_organization: request.nominated_organization || '',
        organization: request.organization || '',
        nominated_name: request.nominated_name || '',
        notes: request.notes || '',
        breakdowns: breakdownsArray,
      });
    }
  }, [request]);

  const handleClose = () => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("request_id");
      return newParams;
    });
    setIsEditing(false);
  };

  const onSubmitEditingForm = async (e: React.FormEvent<HTMLFormElement>) => {
    if (e?.preventDefault) { e.preventDefault(); }
    if (!request_id || !request) { return; }
    if (!isEditing) { return; }
    if (isSaving) { return; }
    setIsSaving(true);

    try {
      const updateResult = await updateRequest({
        id: request_id,
        nominated_organization: formData.nominated_organization,
        organization: formData.organization,
        nominated_name: formData.nominated_name,
        has_been_granted: formData.has_been_granted,
        has_been_dispersed: formData.has_been_dispersed,
        has_been_reconciled: formData.has_been_reconciled,
        notes: (!!request.notes && !formData.notes) ? null : formData.notes,
        breakdowns: formData.breakdowns.map(b => ({
          id: b.id,
          name: b.name,
          amount: b.amount ? safeParseDecimal({})(b.amount).toString() : '0',
          status: b.status,
        })),
      });

      if (!updateResult.success) {
        throw updateResult;
      }

      toast({
        title: "Updated"
      })

      setIsEditing(false);
    } catch (error: any) {
      console.error("Failed to update form", error);
      toast({
        title: "Failed to update"
      })
    } finally {
      setIsSaving(false);
    }

  };

  if (!request_id) return null;

  const isOpen = !!request_id;

  const loadingOrErrorUi = GenerateLoadingOrError([requestResult]);

  return (
    <>
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto xl:max-w-[80vw] lg:max-w-[80vw]">
        {loadingOrErrorUi ? (
          loadingOrErrorUi
        ) : !request ? (
          <div className="p-4 text-center">Request not found</div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Birthday Fund Request</span>
              </DialogTitle>
              <DialogDescription>
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 my-4">
              <Card>
                <CardHeader>
                  <CardTitle>Nominator Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <Label className="font-semibold">Name</Label>
                    <p>{request.survey.data.nominator_first_name} {request.survey.data.nominator_last_name}</p>
                  </div>
                  <div>
                    <Label className="font-semibold">Email</Label>
                    <p>{request.survey.data.email}</p>
                  </div>
                  <div>
                    <Label className="font-semibold">Phone</Label>
                    <p>{request.survey.data.phone}</p>
                  </div>
                  <div>
                    <Label className="font-semibold">Nominating Organization</Label>
                    <p>{request.survey.data.nominating_organization}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Guardian Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <Label className="font-semibold">Name</Label>
                    <p>{request.survey.data.guardian_first_name}</p>
                  </div>
                  <div>
                    <Label className="font-semibold">Phone</Label>
                    <p>{request.survey.data.guardian_phone_number}</p>
                  </div>
                  {request.survey.data.notes && (
                    <div>
                      <Label className="font-semibold">Notes</Label>
                      <p className="whitespace-pre-wrap">{request.survey.data.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Child Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <Label className="font-semibold">Birthday</Label>
                    <p>{request.survey.data.birthday_month_of_child} {request.survey.data.birthday_day_of_child}</p>
                  </div>
                  <div>
                    <Label className="font-semibold">Age</Label>
                    <p>{request.survey.data.child_age || "N/A"}</p>
                  </div>
                  <div>
                    <Label className="font-semibold">Party Location</Label>
                    <p>{request.survey.data.party_location || "N/A"}</p>
                  </div>
                  {(request.survey.data.game_date || request.survey.data.game_party_date) && (
                    <div>
                      <Label className="font-semibold">Game/Party Date</Label>
                      <p className="whitespace-pre-wrap">{request.survey.data.game_date ?? request.survey.data.game_party_date}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="my-4">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Request Status</span><span>                <Badge className={getStatusBadgeColor(request.status)}>
                    {getReadableStatusLabel(request.status)}
                  </Badge></span>
                  {!isEditing && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setShowRawContent(true)}>
                        View Original Email
                      </Button>
                      <Button size="sm" onClick={() => setIsEditing(true)}>
                        Edit Request
                      </Button>
                    </div>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <form onSubmit={onSubmitEditingForm}>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Label htmlFor="nominated_organization">Nominated Organization</Label>
                        <Input
                          id="nominated_organization"
                          value={formData.nominated_organization || ""}
                          onChange={(e) => setFormData({ ...formData, nominated_organization: e.target.value })}
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <Label htmlFor="organization">Organization</Label>
                        <Input
                          id="organization"
                          value={formData.organization || ""}
                          onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="granted"
                          checked={formData.has_been_granted}
                          onCheckedChange={(checked) => setFormData({ ...formData, has_been_granted: !!checked })}
                        />
                        <Label htmlFor="granted">Request Granted</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          disabled={!formData.has_been_granted}
                          id="dispersed"
                          checked={formData.has_been_dispersed}
                          onCheckedChange={(checked) => setFormData({ ...formData, has_been_dispersed: !!checked })}
                        />
                        <Label htmlFor="dispersed">Funds Dispersed</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          disabled={!formData.has_been_granted}
                          id="has_been_reconciled"
                          checked={formData.has_been_reconciled}
                          onCheckedChange={(checked) => setFormData({ ...formData, has_been_reconciled: !!checked })}
                        />
                        <Label htmlFor="has_been_reconciled">Funds Reconciled</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Label htmlFor="notes">Notes</Label>
                        <Input
                          id="notes"
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        />
                      </div>
                      <div className="flex justify-end gap-4">
                        <Button disabled={isSaving} onClick={() => setIsEditing(false)} variant={"secondary"}>
                          Exit
                        </Button>
                        <Button type="submit" disabled={isSaving}>
                          {isSaving ? "Saving" : "Save"}
                        </Button>
                      </div>
                    </div>
                  </form>
                ) : (
                  <div className="grid grid-cols-4 gap-x-6 gap-y-3">
                    <div>
                      <Label className="font-semibold text-xs">Nominated Organization</Label>
                      <p className="text-sm">{request.nominated_organization || "Not specified"}</p>
                    </div>
                    <div>
                      <Label className="font-semibold text-xs">Organization</Label>
                      <p className="text-sm">{request.organization || "Not specified"}</p>
                    </div>
                    <div>
                      <Label className="font-semibold text-xs">Request Granted</Label>
                      <p className="text-sm">{request.has_been_granted ? "Yes" : "No"}</p>
                    </div>
                    <div>
                      <Label className="font-semibold text-xs">Funds Dispersed</Label>
                      <p className="text-sm">{request.has_been_dispersed ? "Yes" : "No"}</p>
                    </div>
                    <div>
                      <Label className="font-semibold text-xs">Amount Projected</Label>
                      <p className="text-sm">{request.amount_projected ? formatCurrency(parseFloat(request.amount_projected)) : "-"}</p>
                    </div>
                    <div>
                      <Label className="font-semibold text-xs">Amount Granted</Label>
                      <p className="text-sm">{request.amount_granted ? formatCurrency(parseFloat(request.amount_granted)) : "-"}</p>
                    </div>
                    <div>
                      <Label className="font-semibold text-xs">Funds Reconciled</Label>
                      <p className="text-sm">{request.has_been_reconciled ? "Yes" : "No"}</p>
                    </div>
                    <div>
                      <Label className="font-semibold text-xs">Created At</Label>
                      <p className="text-sm">{moment.tz(request.created_at, CommonDestinationTimezone).format("MM/DD/YYYY")}</p>
                    </div>
                    <div>
                      <Label className="font-semibold text-xs">Granted Date</Label>
                      <p className="text-sm">{request.granted_date ? moment.tz(request.granted_date, CommonDestinationTimezone).format("MM/DD/YYYY") : "--"}</p>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="font-semibold text-xs">Notes</Label>
                      <p className="text-sm whitespace-pre-wrap">{request.notes ?? "-"}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Breakdowns Section */}
            <Card className="my-4">
              <CardHeader>
                <CardTitle>Cost Breakdowns</CardTitle>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <div className="space-y-4">
                    {formData.breakdowns.length > 0 ? (
                      (() => {
                        // Group breakdowns by name for editing
                        const groupedByName = formData.breakdowns.reduce((acc, breakdown) => {
                          const name = breakdown.name || 'Unnamed';
                          if (!acc[name]) {
                            acc[name] = { projected: null as BreakdownFormData | null, granted: null as BreakdownFormData | null };
                          }
                          acc[name][breakdown.status] = breakdown;
                          return acc;
                        }, {} as Record<string, Record<'projected' | 'granted', BreakdownFormData | null>>);

                        const updateBreakdown = (name: string, status: 'projected' | 'granted', amount: string) => {
                          const newBreakdowns = formData.breakdowns.map(b => {
                            if (b.name === name && b.status === status) {
                              return { ...b, amount };
                            }
                            return b;
                          });
                          setFormData({ ...formData, breakdowns: newBreakdowns });
                        };

                        const sortedNames = Object.keys(groupedByName).sort((a, b) => a.localeCompare(b));

                        return (
                          <div className="space-y-1">
                            {/* Table header */}
                            <div className="grid grid-cols-[2fr_1fr_1fr] gap-2 text-xs font-medium text-muted-foreground border-b pb-2 mb-2">
                              <div>Name</div>
                              <div className="text-right">Projected ($)</div>
                              <div className="text-right">Granted ($)</div>
                            </div>
                            {sortedNames.map((name) => {
                              const breakdowns = groupedByName[name];
                              return (
                                <div key={name} className="grid grid-cols-[2fr_1fr_1fr] gap-2 items-center py-2 border-b last:border-0">
                                  <div className="font-medium">{name}</div>
                                  <div className="text-right">
                                    {breakdowns.projected ? (
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={breakdowns.projected.amount}
                                        onChange={(e) => updateBreakdown(name, 'projected', e.target.value)}
                                        className="text-right"
                                        placeholder="0.00"
                                      />
                                    ) : (
                                      <span className="text-muted-foreground">-</span>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    {breakdowns.granted ? (
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={breakdowns.granted.amount}
                                        onChange={(e) => updateBreakdown(name, 'granted', e.target.value)}
                                        className="text-right"
                                        placeholder="0.00"
                                      />
                                    ) : (
                                      <span className="text-muted-foreground">-</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()
                    ) : (
                      <p className="text-muted-foreground text-sm">No breakdowns yet.</p>
                    )}
                  </div>
                ) : (
                  <div>
                    {formData.breakdowns.length > 0 && request?.breakdowns ? (
                      (() => {
                        // Get breakdown data with certificates from the request
                        const breakdownsWithCerts = Array.isArray(request.breakdowns) 
                          ? request.breakdowns 
                          : request.breakdowns.id ? [request.breakdowns] : [];

                        // Group breakdowns by name with certificate info
                        const groupedByName = formData.breakdowns.reduce((acc, breakdown) => {
                          const name = breakdown.name || 'Unnamed';
                          if (!acc[name]) {
                            acc[name] = { projected: 0, granted: 0, projectedCert: null, grantedCert: null };
                          }
                          acc[name][breakdown.status] = parseFloat(breakdown.amount) || 0;
                          
                          // Find the matching breakdown with certificate data
                          const breakdownWithCert = breakdownsWithCerts.find((b: any) => b.id === breakdown.id);
                          if (breakdownWithCert?.certificates && breakdownWithCert.certificates.length > 0) {
                            const cert = breakdownWithCert.certificates[0];
                            if (breakdown.status === 'projected') {
                              acc[name].projectedCert = cert;
                            } else if (breakdown.status === 'granted') {
                              acc[name].grantedCert = cert;
                            }
                          }
                          
                          return acc;
                        }, {} as Record<string, Record<'projected' | 'granted', number> & { projectedCert: any, grantedCert: any }>);

                        const totals = {
                          projected: formData.breakdowns.filter(b => b.status === 'projected').reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0),
                          granted: formData.breakdowns.filter(b => b.status === 'granted').reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0),
                        };

                        const sortedNames = Object.keys(groupedByName).sort((a, b) => a.localeCompare(b));

                        return (
                          <div className="space-y-1">
                            {/* Mini table header */}
                            <div className="grid grid-cols-[2fr_1fr_1fr_1.5fr] gap-2 text-xs font-medium text-muted-foreground border-b pb-1">
                              <div>Item</div>
                              <div className="text-right">Projected</div>
                              <div className="text-right">Granted</div>
                              <div className="text-right">Certificate</div>
                            </div>
                            {sortedNames.map((name) => {
                              const data = groupedByName[name];
                              const cert = data.projectedCert || data.grantedCert;
                              return (
                                <div key={name} className="grid grid-cols-[2fr_1fr_1fr_1.5fr] gap-2 text-sm py-1 border-b last:border-0">
                                  <div className="font-medium">{name}</div>
                                  <div className="text-right">{data.projected > 0 ? formatCurrency(data.projected) : '-'}</div>
                                  <div className="text-right">{data.granted > 0 ? formatCurrency(data.granted) : '-'}</div>
                                  <div className="text-right text-xs text-muted-foreground">
                                    {cert ? (
                                      <span>{cert.certificate_id} ({cert.status})</span>
                                    ) : (
                                      <span>-</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            {/* Totals row */}
                            <div className="grid grid-cols-[2fr_1fr_1fr_1.5fr] gap-2 text-sm font-semibold pt-1 border-t mt-1">
                              <div>Total</div>
                              <div className="text-right">{formatCurrency(totals.projected)}</div>
                              <div className="text-right">{formatCurrency(totals.granted)}</div>
                              <div></div>
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      <p className="text-muted-foreground text-sm">No breakdowns recorded.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {request.survey.errors && request.survey.errors.length > 0 && (
              <Card className="my-4 bg-red-50">
                <CardHeader>
                  <CardTitle>Survey Errors</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc pl-5 space-y-1">
                    {request.survey.errors.map((error: any, index: number) => (
                      <li key={index} className="text-red-600">{error}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <DialogFooter>
              {isEditing ? (
                <>
                </>
              ) : (
                <Button onClick={handleClose}>
                  Close
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
    
    <Dialog open={showRawContent} onOpenChange={setShowRawContent}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Raw S3 Document Content</DialogTitle>
          <DialogDescription>
            Original form submission data
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4">
          {documentResult?.isLoading ? (
            <div className="text-center py-4">Loading raw content...</div>
          ) : documentResult?.error ? (
            <div className="text-red-600 py-4">Error loading raw content</div>
          ) : documentResult?.data?.data?.document_content ? (
            <pre className="bg-gray-100 p-4 rounded overflow-x-auto text-sm font-mono">
              {documentResult.data.data.document_content}
            </pre>
          ) : (
            <div className="text-center py-4 text-gray-500">No raw content available</div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => setShowRawContent(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
